/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import Protocols from '@cedalo/protocols';
import qs from 'query-string';
import React, { useEffect, useReducer, useState, useMemo, useRef } from 'react';
import { graphManager } from '../../GraphManager';
import { accessManager, RESOURCE_ACTIONS } from '../../helper/AccessManager';
import ConfigManager from '../../helper/ConfigManager';
import gatewayClient from '../../helper/GatewayClient';
import MachineHelper from '../../helper/MachineHelper';
import { Path } from '../../helper/Path';
import LOAD_APP_QUERY from './load-app.gql';
import CONNECTION_CHANGES_SUBSCRIPTION from './connection-changes.gql';
import { functionStrings } from '../../languages/FunctionStrings';
import SheetParserContext from '../../SheetParserContext';
import { withUser } from '../../UserProvider';
import { runQuery, useSubscription } from './GraphQLWSClient';
import { rename } from './AppGraphQL';
import { useForkRef } from '@mui/material';

const mapWhereId = (array, id, fn) => array.map((el) => (el.id === id ? fn(el) : el));

export const AppContext = React.createContext(null);

const { EVENTS } = Protocols.GatewayMessagingProtocol;
const CONFIG = ConfigManager.config.gatewayClientConfig;

window.onbeforeunload = () => {
	graphManager.removeSelection();
};

const JSGEventHandler = {
	handleStreamSheetRemovedEvent: (event) => {
		const { srcId: machineId, streamsheet } = event;
		// remove current selection to prevent error in setJsgState-dispatch if called inside a dispatch phase (DL-5437)
		// TODO: still required?
		const selectionProvider = graphManager.getGraphViewer().getSelectionProvider();
		selectionProvider.clearSelection();
		// newstate.machine.streamsheets = state.machine.streamsheets.filter((sts) => sts.id !== streamsheet.id);
		// update model:
		graphManager.deleteStreamSheet(streamsheet.id);
		graphManager.redraw();
	},
	handleStreamSheetAddedEvent: (event) => {
		const { streamsheet } = event;
		if (streamsheet) {
			// update state:
			// TODO: do we need the streamsheets in the appstate?
			// newstate.machine.streamsheets.push({ ...streamsheet });
			// update model:
			graphManager.createStreamSheet(streamsheet);
			graphManager.redraw();
		}
	},
	handleSelectionEvent: (event) => {
		const { selection = {} } = event;
		// currently synchronize cell selection only
		if (selection.name === 'command.server.SelectCellsCommand') {
			const graph = graphManager.getGraph();
			const cmd = graph ? JSG.SelectCellsCommand.createFromObject(selection, { graph }) : undefined;
			if (cmd) graphManager.execute(cmd, true);
		}
	},
	handleStreamUpdatedEvent: (event) => {
		const { src, srcId, settings } = event;
		if (src === 'streamsheet' && srcId && settings) {
			const sheetsContainer = graphManager.getGraph().streamSheetsContainer;
			const sht = sheetsContainer.find(srcId);
			if (sht) {
				sht.updateStreamSheetSettings(settings);
				graphManager.sendNotification('streamsheet_settings_notification', sht);
			}
			graphManager.redraw();
		}
	},
	handleMessagePopEvent: (event) => {
		if (event.src === 'inbox') {
			const { message, streamsheetId, totalSize } = event;
			graphManager.removeInboxMessage(streamsheetId, message, totalSize);
			graphManager.redraw();
		} else if (event.src === 'outbox') {
			graphManager.removeOutboxMessage(event.message);
			graphManager.redraw();
		}
	},
	handleMessagePutEvent: (event) => {
		if (event.src === 'inbox') {
			const { message, streamsheetId, totalSize } = event;
			graphManager.addInboxMessage(streamsheetId, message, totalSize);
			graphManager.redraw();
			// if (message) {
			// 	graphManager.addInboxMessage(streamsheetId, message, totalSize);
			// 	graphManager.redraw();
			// }
		}
		// else if (event.src === 'outbox') {
		// 	graphManager.addOutboxMessage(event.message);
		// }
	},
	handleMessageBoxClear: (event) => {
		if (event.src === 'inbox') {
			graphManager.clearInbox(event.streamsheetId);
			graphManager.redraw();
		} else if (event.src === 'outbox') {
			graphManager.clearOutbox();
			graphManager.redraw();
		}
	},
	handleMachineSettingsEvent: (event, oldLocale) => {
		const { settings } = event;
		if (settings.locale && settings.locale !== oldLocale) {
			graphManager.setMachineLanguage(settings.locale);
		}
		graphManager.updateMachineSettings(settings);
		graphManager.graph.layout();
		graphManager.graph.machineContainer.layout();
		graphManager.graph.markDirty();
		graphManager.redraw();
	},
	handleMachineStateEvent: (event) => {
		graphManager.updateMachineState(event.state);
	},
	handleNamedCellsUpdateEvent: (event) => {
		const { namedCells = {} } = event;
		graphManager.updateMachineNamedCells(namedCells);
		graphManager.sendNotification('NAMED_CELLS_NOTIFICATION');
	},
	handleSheetUpdateEvent: (event) => {
		const { srcId: streamsheetId } = event;
		const { cells, properties, shapes, namedCells } = event.sheet;
		const sheetsContainer = graphManager.getGraph().streamSheetsContainer;
		const sheet = sheetsContainer.find(streamsheetId);
		if (sheet) {
			sheet.updateSheet(cells, shapes, namedCells, properties);
			sheetsContainer.updateShapes(streamsheetId, shapes);
		}

		graphManager.updateUI();
		graphManager.redraw();
	},
	handleUserLeftEvent: (event) => {
		const { sessionId, user } = event;
		const graph = graphManager.getGraph();
		if (graph._userSelections.remove(JSG.StreamSheet.createSelectionId(user, sessionId))) {
			graphManager.redraw();
		}
	},
	handleCustomInfoUpdatedEvent: (event) => {
		const { src, srcId, custom } = event;
		if (srcId) {
			if (src === 'machine') {
				// update graph model:
				graphManager.updateMachineCustomInfo(custom);
				graphManager.redraw();
			} else if (src === 'streamsheet') {
				graphManager.updateStreamSheetCustomInfo(srcId, custom);
				graphManager.redraw();
			}
		}
	},
	handleMessageAttachedEvent: (event) => {
		graphManager.selectInboxMessage(event.srcId, event.messageId);
	},
	handleMessageDetachedEvent: (event) => {
		graphManager.selectInboxMessage(event.srcId, event.messageId, true);
		// graphManager.selectInboxMessage(event.srcId, null);
	},
	handleMachineFuntionsEvent: (event) => {
		JSG.FormulaParser.context = new SheetParserContext(event.functionDefinitions.map((def) => def.name));
	},
	handleMachineStepEvent: (event) => {
		const { machineId, outbox, srcId, streamsheets } = event;
		try {
			// graphManager._performance.events.add({});
			// graphManager.updatePerformance();
			graphManager.setDrawingDisabled(true);
			streamsheets.forEach((streamsheet) => {
				try {
					const { id, cells, namedCells, shapes, inbox, loop, properties, stats } = streamsheet;
					graphManager.handleStreamSheetStep(
						id,
						loop.currentPath,
						cells,
						namedCells,
						shapes,
						// TODO: improve, outbox does not need to be updated for each streamsheet
						event.outbox,
						stats,
						inbox,
						properties
					);
				} catch (error) {
					// this can happen if the machine step event comes
					// before the machine is loaded on the client side,
					// usually when the machine is running very fast
				}
			});
			graphManager.handleMachineStep(outbox);
			graphManager.setDrawingDisabled(false);
		} catch (error) {
			console.error(error);
		} finally {
			// finally confirm:
			gatewayClient.confirmProcessedMachineStep(machineId || srcId);
		}
	},
	handleConnectionSheetEvent: (event) => {
		const { event: type, data, timestamp } = event;
	},
	handleSubscribedEvent: (/* event */) => {
		const viewer = graphManager.getGraphViewer();
		const activeView = graphManager.getActiveSheetView();
		activeView.sendCurrentSelection(viewer);
	},
	handleUnsubscribedEvent: (event) => {
		// remove unsubscribed client from current selection:
		const { sessionId, user } = event;
		const graph = graphManager.getGraph();
		if (graph._userSelections.remove(JSG.StreamSheet.createSelectionId(user, sessionId))) {
			graphManager.redraw();
		}
	}
};

// function reloadCurrentMachine() {
// 	const currentMachine = store.getState().monitor.machine;
// 	const currentMachineId = currentMachine && currentMachine.id;
// 	if (currentMachineId) {
// 		store.dispatch(loadSubscribeMachine(currentMachineId, null));
// 	}
// }

const unsubscribe = async (machineId) => {
	// return async (dispatch) => {
	graphManager.removeSelection();
	try {
		await gatewayClient.unsubscribeMachine(machineId);
	} catch (error) {
		console.error(error);
	}
};
// }

const loadSubscribeMachine = async (machineId: string, options: { settings?: any; scope?: any } = {}) => {
	const { settings, scope } = (options || {}) as { settings?: any; scope?: any };
	try {
		const response = await gatewayClient.loadSubscribeMachine(machineId, settings, scope);
		if (response.machineserver.error) {
			console.error(response.machineserver.error);
			return null;
		}
		functionStrings.addFunctionsHelp(response.machineserver.machine.functionsHelp);
		JSG.FormulaParser.context = new SheetParserContext(response.machineserver.machine.functionDefinitions);
		const canvasElement = document.createElement('canvas');
		// canvasElement.id = `canvas-${machineId}`;
		canvasElement.id = `canvas`;
		canvasElement.width = 800;
		canvasElement.height = 450;
		canvasElement.tabIndex = 0;
		canvasElement.style.width = '100%';
		canvasElement.style.height = '100%';
		canvasElement.style.outline = 'none';

		graphManager.setDrawingDisabled(true);
		graphManager.createEditor(canvasElement);
		graphManager.loadGraph(response.machineserver.machine);
		graphManager.setDrawingDisabled(false);
		if (!response.machineserver.templateId) {
			// dispatch(receiveMachineLoad(response));
		}
		return response.machineserver;
	} catch (error) {
		console.error(error);
		return null;
	}
};

export type Connection = any;
export type MachineSettings = any;
export type Machine = {
	state: string;
	canEdit: boolean;
	settings: MachineSettings;
	connections: Connection[];
};
export type ResetAction = { type: 'reset'; data?: undefined };
export type InitAction = { type: 'init'; data: { machine: Machine; machineLoadResponse: any; canEditApp: boolean } };
export type RenameMachineAction = { type: 'rename-machine'; data: string };
export type SetCanEditAction = { type: 'set-can-edit'; data: boolean };
export type SetMachineStateAction = { type: 'set-machine-state'; data: string };
export type UpdateMachineSettingsAction = { type: 'update-machine-settings'; data: MachineSettings };
export type AddConnectionAction = { type: 'add-connection'; data: any };
export type RemoveConnectionAction = { type: 'remove-connection'; data: any };
export type UpdateConnectionAction = { type: 'update-connection'; data: any };
export type UpdateConnectionStatusAction = { type: 'update-connection-status'; data: any };

export type AppStateAction =
	| RenameMachineAction
	| SetCanEditAction
	| UpdateMachineSettingsAction
	| ResetAction
	| InitAction
	| SetMachineStateAction
	| AddConnectionAction
	| RemoveConnectionAction
	| UpdateConnectionAction
	| UpdateConnectionStatusAction;

function assertUnreachable(action: never): never;
function assertUnreachable(action: AppStateAction): never {
	throw new Error(`Unkown AppStateAction ${action.type}`);
}

const appStateReducer = (state, action: AppStateAction) => {
	const { type, data } = action;
	if (type === 'init') {
		const { connections, ...machineData } = data.machine;
		const { name, id, state, settings, extensionSettings } = data.machineLoadResponse;
		return {
			canEditApp: data.canEditApp,
			machine: {
				...machineData,
				name,
				id,
				state,
				settings,
				extensionSettings
			},
			connections
		};
	}
	if (type === 'reset') return null;
	if (state) {
		switch (type) {
			// case 'reset':
			// 	return null;
			// case 'init': {
			// 	const { connections, canEdit, scope } = data.machine;
			// 	const { name, id, state, settings, extensionSettings } = data.machineLoadResponse;
			// 	return {
			// 		canEditApp: data.canEditApp,
			// 		machine: {
			// 			canEdit,
			// 			scope,
			// 			name,
			// 			id,
			// 			state,
			// 			settings,
			// 			extensionSettings
			// 		},
			// 		connections
			// 	};
			// }
			case 'rename-machine':
				// When creating a new machine, we get the rename event without the machine being inited properly
				return state ? { ...state, machine: { ...state.machine, name: data } } : state;
			case 'set-can-edit':
				return { ...state, canEditApp: data };
			case 'update-machine-settings':
				return {
					...state,
					machine: {
						...state.machine,
						settings: {
							...state.machine.settings,
							...data
						}
					}
				};
			case 'set-machine-state':
				return {
					...state,
					machine: {
						...state.machine,
						state: data
					}
				};
			case 'add-connection':
				return {
					...state,
					connections: [...state.connections, data]
				};
			case 'remove-connection':
				return {
					...state,
					connections: state.connections.filter(({ id }) => id !== data)
				};
			case 'update-connection':
				return {
					...state,
					connections: mapWhereId(state.connections, data.id, (c) => ({ ...c, ...data }))
				};
			case 'update-connection-status':
				return {
					...state,
					connections: mapWhereId(state.connections, data.id, (c) => ({ ...c, status: data.status }))
				};
			// case 'update-streamsheet': {
			// 	const { src, srcId, settings } = event;
			// 	if (src === 'streamsheet' && srcId && settings) {
			// 		settings.streamsheetId = srcId;
			// 		const { streamsheetId, name, loop, trigger, inbox, sheet } = settings;
			// 		newstate.machine.streamsheets.map((streamsheet) => {
			// 			if (streamsheet.id === streamsheetId) {
			// 				// update state:
			// 				streamsheet.name = name;
			// 				streamsheet.loop = { ...streamsheet.loop, ...loop };
			// 				streamsheet.trigger = { ...streamsheet.trigger, ...trigger };
			// 				streamsheet.inbox.stream = inbox.stream
			// 					? { ...streamsheet.inbox.stream, ...inbox.stream }
			// 					: null;
			// 				streamsheet.sheet.settings = { ...streamsheet.sheet.settings, ...sheet };
			// 			}
			// 			return streamsheet;
			// 		});
			// 	}
			// }
			// case 'update-custom-info': {
			// 	const { src, srcId, custom } = data;
			// 	if (srcId) {
			// 		if (src === 'machine') {
			// 			return { ...state, machine: { ...state.machine, custom } };
			// 		} else if (src === 'streamsheet') {
			// 			const streamsheet = newstate.machine.streamsheets.find((sts) => sts.id === srcId);
			// 			if (streamsheet) {
			// 				// update state:
			// 				streamsheet.custom = { ...custom };
			// 			}
			// 		}
			// 	}
			// }

			default:
				assertUnreachable(action);
		}
	}
};

export const AppProvider = withUser(({ user }) => ({ hadAppTour: user.hadAppTour }))((props) => {
	const { machineId, locale, noEdit = false, children, hadAppTour } = props;
	const [isConnected, setConnected] = useState<boolean>(false);
	const [appState, dispatch] = useReducer(appStateReducer, null);

	useEffect(() => {
		// gatewayClient.on('service', (/* event */) => {
		// 	getMetaInformationAndDispatch();
		// });
		// gatewayClient.on('redirect', () => {
		// 	accessManager.logoutUI(true);
		// });
		const config = {
			...CONFIG,
			pathname: window.location.pathname,
			token: accessManager.authToken
		};

		const machineSettingsHandler = (event) => {
			dispatch({ type: 'update-machine-settings', data: event.settings });
			JSGEventHandler.handleMachineSettingsEvent(event, appState?.machine?.settings?.locale);
		};
		const machineStateHandler = (event) => {
			dispatch({ type: 'set-machine-state', data: event.state });
			JSGEventHandler.handleMachineStateEvent(event);
		};
		const machineRenameHandler = (event) => {
			graphManager.updateMachineName(event.name);
			dispatch({ type: 'rename-machine', data: event.name });
		};
		const gatewayDisconnecteHandler = () => setConnected(false);
		gatewayClient
			.connect(config)
			.then(() => {
				// gatewayClient.on(EVENTS.SESSION_INIT_EVENT, (event) => {
				// 	sessionStorage.setItem('sessionId', event.session.id);
				// });
				// gatewayClient.on(EVENTS.USER_JOINED_EVENT, (event) => {
				// Not used!
				// 	handleUserJoinedEvent(event);
				// 	dispatch(receiveUserJoined(event));
				// });
				gatewayClient.on(EVENTS.GATEWAY_DISCONNECTED_EVENT, gatewayDisconnecteHandler);
				// TODO: does this still handle logout? see userReducer
				gatewayClient.on(EVENTS.USER_LEFT_EVENT, JSGEventHandler.handleUserLeftEvent);
				gatewayClient.on(EVENTS.MACHINE_STEP_EVENT, JSGEventHandler.handleMachineStepEvent);
				gatewayClient.on(EVENTS.MACHINE_RENAME_EVENT, machineRenameHandler);
				gatewayClient.on(EVENTS.MACHINE_FUNCTIONS_EVENT, JSGEventHandler.handleMachineFuntionsEvent);
				gatewayClient.on(EVENTS.MACHINE_STATE_EVENT, machineStateHandler);
				gatewayClient.on(EVENTS.CONNECTION_SHEET_EVENT, JSGEventHandler.handleConnectionSheetEvent);
				gatewayClient.on(EVENTS.MESSAGE_BOX_CLEAR, JSGEventHandler.handleMessageBoxClear);
				gatewayClient.on(EVENTS.MESSAGE_PUT, JSGEventHandler.handleMessagePutEvent);
				gatewayClient.on(EVENTS.MESSAGE_POP, JSGEventHandler.handleMessagePopEvent);
				// gatewayClient.on(EVENTS.MESSAGE_CHANGED, handleMessageChangedEvent); Not used!
				gatewayClient.on(EVENTS.STREAMSHEET_STREAM_UPDATED, JSGEventHandler.handleStreamUpdatedEvent);
				gatewayClient.on(EVENTS.SHEET_UPDATE_EVENT, JSGEventHandler.handleSheetUpdateEvent);
				// TODO: replace with inbox select event when available from machine server
				gatewayClient.on(EVENTS.STREAMSHEET_MESSAGE_ATTACHED, JSGEventHandler.handleMessageAttachedEvent);
				gatewayClient.on(EVENTS.STREAMSHEET_MESSAGE_DETACHED, JSGEventHandler.handleMessageDetachedEvent);
				// SSF:
				gatewayClient.on(EVENTS.MACHINE_SETTINGS_EVENT, machineSettingsHandler);
				gatewayClient.on(EVENTS.NAMED_CELLS_EVENT, JSGEventHandler.handleNamedCellsUpdateEvent);
				gatewayClient.on(EVENTS.CUSTOM_INFO_UPDATED, JSGEventHandler.handleCustomInfoUpdatedEvent);
				gatewayClient.on(EVENTS.STREAMSHEET_ADDED, JSGEventHandler.handleStreamSheetAddedEvent);
				gatewayClient.on(EVENTS.STREAMSHEET_REMOVED, JSGEventHandler.handleStreamSheetRemovedEvent);
				gatewayClient.on(EVENTS.SELECTION_EVENT, JSGEventHandler.handleSelectionEvent);
				gatewayClient.on(EVENTS.MACHINE_SUBSCRIBED_EVENT, JSGEventHandler.handleSubscribedEvent);
				gatewayClient.on(EVENTS.MACHINE_UNSUBSCRIBED_EVENT, JSGEventHandler.handleUnsubscribedEvent);
				// gatewayClient.on(EVENTS.USER_LEFT_EVENT, JSGEventHandler.handleUserLeft);
			})
			.then(() => setConnected(true))
			.catch((e) => {
				console.log(e);
			});
		// TODO: should we disconnect when we leave the AppPage?
		return () => {
			gatewayClient.off(EVENTS.GATEWAY_DISCONNECTED_EVENT, gatewayDisconnecteHandler);
			gatewayClient.off(EVENTS.USER_LEFT_EVENT, JSGEventHandler.handleUserLeftEvent);
			gatewayClient.off(EVENTS.MACHINE_STEP_EVENT, JSGEventHandler.handleMachineStepEvent);
			gatewayClient.off(EVENTS.MACHINE_RENAME_EVENT, machineRenameHandler);
			gatewayClient.off(EVENTS.MACHINE_FUNCTIONS_EVENT, JSGEventHandler.handleMachineFuntionsEvent);
			gatewayClient.off(EVENTS.MACHINE_STATE_EVENT, machineStateHandler);
			gatewayClient.off(EVENTS.CONNECTION_SHEET_EVENT, JSGEventHandler.handleConnectionSheetEvent);
			gatewayClient.off(EVENTS.MESSAGE_BOX_CLEAR, JSGEventHandler.handleMessageBoxClear);
			gatewayClient.off(EVENTS.MESSAGE_PUT, JSGEventHandler.handleMessagePutEvent);
			gatewayClient.off(EVENTS.MESSAGE_POP, JSGEventHandler.handleMessagePopEvent);
			gatewayClient.off(EVENTS.STREAMSHEET_STREAM_UPDATED, JSGEventHandler.handleStreamUpdatedEvent);
			gatewayClient.off(EVENTS.SHEET_UPDATE_EVENT, JSGEventHandler.handleSheetUpdateEvent);
			gatewayClient.off(EVENTS.STREAMSHEET_MESSAGE_ATTACHED, JSGEventHandler.handleMessageAttachedEvent);
			gatewayClient.off(EVENTS.STREAMSHEET_MESSAGE_DETACHED, JSGEventHandler.handleMessageDetachedEvent);
			gatewayClient.off(EVENTS.MACHINE_SETTINGS_EVENT, machineSettingsHandler);
			gatewayClient.off(EVENTS.NAMED_CELLS_EVENT, JSGEventHandler.handleNamedCellsUpdateEvent);
			gatewayClient.off(EVENTS.CUSTOM_INFO_UPDATED, JSGEventHandler.handleCustomInfoUpdatedEvent);
			gatewayClient.off(EVENTS.STREAMSHEET_ADDED, JSGEventHandler.handleStreamSheetAddedEvent);
			gatewayClient.off(EVENTS.STREAMSHEET_REMOVED, JSGEventHandler.handleStreamSheetRemovedEvent);
			gatewayClient.off(EVENTS.SELECTION_EVENT, JSGEventHandler.handleSelectionEvent);
			gatewayClient.off(EVENTS.MACHINE_SUBSCRIBED_EVENT, JSGEventHandler.handleSubscribedEvent);
			gatewayClient.off(EVENTS.MACHINE_UNSUBSCRIBED_EVENT, JSGEventHandler.handleUnsubscribedEvent);
			// gatewayClient.off(EVENTS.USER_LEFT_EVENT, JSGEventHandler.handleUserLeft);
		};
	}, []);

	const loadMachine = async () => {
		dispatch({ type: 'reset' });
		const query = qs.parse(window.location.search);
		const newMachineName = query.machineName;
		const scope = { id: query.scope };
		try {
			const { machine, templateId } = await loadSubscribeMachine(machineId, {
				settings: { locale },
				scope
			});
			const createdFromTemplate = !!templateId;
			if (createdFromTemplate) {
				await rename(machine.id, newMachineName);
				console.log(`Rerouting to machine ${machine.id}`);
				const newURL = Path.app(machine.id);
				window.location.replace(newURL);
				return;
			}
			// will probably always/often fail for shared machine due to wrong scope??
			const result = await runQuery(LOAD_APP_QUERY, { machineId });
			dispatch({
				type: 'init',
				data: {
					canEditApp: result.machine.canEdit && !noEdit,
					machine: result.machine,
					machineLoadResponse: machine
				}
			});
			if (noEdit || query.preview) {
				// dispatch({ type: 'set-view-mode', data: true });
			} else if (!hadAppTour) {
				// dispatch({ type: 'start-tour', data: 'stream-app' });
			}
		} catch (error) {
			console.log(error);
			console.log(`${error ? error.toString() : ''}`);
		} finally {
			// always confirm load to signal that we are ready to handle events from other clients now:
			await gatewayClient.confirmLoadSubscribeMachine(machineId /*, scope */);
		}
	};

	useEffect(() => {
		if (appState?.machine) {
			dispatch({ type: 'set-can-edit', data: !noEdit && appState?.machine.canEdit });
		}
	}, [noEdit]);

	const [subData, subErrors] = useSubscription(CONNECTION_CHANGES_SUBSCRIPTION, { machineId });
	useEffect(() => {
		if (subErrors) {
			console.error(subErrors);
		}
	}, [subErrors]);
	useEffect(() => {
		if (subData) {
			const eventData = subData.connectionChanged;
			switch (eventData.__typename) {
				case 'ConnectionCreateEvent': {
					dispatch({ type: 'add-connection', data: eventData.connection });
					break;
				}
				case 'ConnectionStatusEvent': {
					dispatch({
						type: 'update-connection-status',
						data: { id: eventData.connectionId, status: eventData.status }
					});
					break;
				}
				case 'ConnectionDeleteEvent': {
					dispatch({ type: 'remove-connection', data: eventData.connectionId });
					break;
				}
				case 'ConnectionUpdateEvent': {
					dispatch({
						type: 'update-connection',
						data: {
							id: eventData.connectionId,
							name: eventData.name,
							lastModified: eventData.lastModified,
							targets: eventData.targets
						}
					});
					break;
				}
				default:
			}
		}
	}, [subData]);

	useEffect(() => {
		if (isConnected && MachineHelper.currentMachineCan(RESOURCE_ACTIONS.VIEW) && machineId) {
			loadMachine();
			return () => unsubscribe(machineId);
		}
		return () => {};
	}, [machineId, isConnected]);
	return (
		<AppContext.Provider value={{ appData: appState, dispatchAppData: dispatch }}>{children}</AppContext.Provider>
	);
});

const selectProps = (selector, props, previous, ownProps) => {
	const newSelection = selector(props, ownProps);
	const newEntries = Object.entries(newSelection);
	return newEntries.length === Object.keys(previous).length && newEntries.every(([k, v]) => v === previous[k])
		? previous
		: newSelection;
};

const AppConsumerWrapper = ({ ownProps, appData, selector, Component }) => {
	const previous = useRef({});
	const selectedProps = selectProps(selector, appData, previous.current, ownProps);
	previous.current = selectedProps;
	return useMemo(() => <Component {...ownProps} {...previous.current} />, [ownProps, previous.current]);
};

export const withAppData =
	(selector: (appData: any, ownProps: any) => any = (x) => {}) =>
	(Component, Fallback?) =>
	(props) =>
		(
			<AppContext.Consumer>
				{({ appData, dispatchAppData }) => {
					if (!appData) {
						return Fallback ? <Fallback {...props} /> : null;
					}
					return (
						<AppConsumerWrapper
							ownProps={props}
							selector={selector}
							appData={{ ...appData, dispatchAppData }}
							Component={Component}
						/>
					);
				}}
			</AppContext.Consumer>
		);
